import { makeStyles } from "tss-react/mui";
import {
  COLORS,
  MIBUILD_PAGE_HEADER_HEIGHT,
  SECTION_HEADER_VARIANTS,
} from "@miplatform/constants";
import { mdiInformationOutline } from "@mdi/js";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * Renders a section header component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.title=""] - The title of the section header.
 * @param {string|ReactNode} [props.subtitle=""] - The subtitle of the section header.
 * @param {string} [props.leftIcon] - The path to the left icon.
 * @param {string} [props.leftIconColor] - The palette color of the left icon, ie success, error, primary.
 * @param {Function} [props.onLeftIconClick] - The click event handler for the left icon.
 * @param {string} [props.rightIcon] - The path to the right icon.
 * @param {string} [props.rightIconColor] - The color of the right icon.
 * @param {Function} [props.onRightIconClick] - The click event handler for the right icon.
 * @param {string} [props.color] - The color of the section header.
 * @param {string} [props.variant] - The variant of the section header.
 * @param {string} [props.titleVariant="body1"] - The variant of the title typography.
 * @param {ReactNode} [props.rightAction] - The custom right action component.
 * @param {boolean} [props.removeMargin=false] - Whether to remove the bottom margin.
 * @param {Object} [props.headerStyle={}] - Custom styles for the header container.
 * @param {boolean} [props.horizontalTitles=false] - Whether to display title and subtitle horizontally.
 * @param {boolean} [props.stickyHeader=false] - Whether the header should be sticky.
 * @param {string} [props.toolTipTitle=""] - The tooltip text for the info icon next to the title.
 * @returns {JSX.Element} The rendered section header component.
 */
export function MiSectionHeader({
  title = "",
  subtitle = "",
  leftIcon,
  leftIconColor = COLORS.PRIMARY,
  leftIconSize = 0.8,
  onLeftIconClick,
  rightIcon,
  rightIconColor,
  onRightIconClick,
  color,
  rightAction,
  variant = SECTION_HEADER_VARIANTS.UNDERLINED,
  titleVariant = "body1",
  removeMargin = false,
  headerStyle = {},
  horizontalTitles = false,
  stickyHeader = false,
  toolTipTitle = "",
  ...rest
}) {
  const { classes } = useStyles({ removeMargin, stickyHeader });
  const theme = useTheme();

  const variantStyles = {
    [SECTION_HEADER_VARIANTS.OUTLINED]: classes.containerOutlined,
    [SECTION_HEADER_VARIANTS.CONTAINED]: classes.containerContained,
    [SECTION_HEADER_VARIANTS.TEXT]: classes.container,
    [SECTION_HEADER_VARIANTS.UNDERLINED]: classes.containerUnderlined,
    [undefined]: classes.container,
  };

  const titleVariants = {
    [SECTION_HEADER_VARIANTS.OUTLINED]: "body1",
    [SECTION_HEADER_VARIANTS.CONTAINED]: "body1",
    [SECTION_HEADER_VARIANTS.TEXT]: "body1",
    [SECTION_HEADER_VARIANTS.UNDERLINED]: "body1",
    [undefined]: "body1",
  };

  return (
    <Box className={variantStyles[variant]}>
      <Box className={classes.header} style={headerStyle} {...rest}>
        <Box className={classes.leftPart}>
          {leftIcon &&
            (onLeftIconClick ? (
              <MiButton
                sx={{ padding: 1 }}
                className={classes.leftIconButton}
                startIcon={{
                  path: leftIcon,
                  iconColor: leftIconColor || theme.palette.primary.main,
                }}
                onClick={onLeftIconClick}
                data-testid="left-icon-button"
              />
            ) : (
              <Box
                backgroundColor={theme.palette[leftIconColor].light}
                borderRadius={2}
                padding={1}
                width={28}
                height={28}
              >
                <MiIcon
                  path={leftIcon}
                  size={leftIconSize}
                  color={theme.palette[leftIconColor].dark}
                  onClick={onLeftIconClick}
                  data-testid="left-icon"
                />
              </Box>
            ))}
          {color && (
            <Box
              data-testid="header-color"
              className={classes.jobTypeColor}
              sx={{ backgroundColor: color }}
            />
          )}
          <Box className={classes.headerText}>
            <Box className={horizontalTitles && classes.flexSpaceBetween}>
              <Box className={classes.titleContainer}>
                <Typography
                  variant={titleVariant || titleVariants[variant]}
                  className={classes.title}
                >
                  {title}
                </Typography>
                {toolTipTitle && (
                  <Tooltip
                    title={toolTipTitle}
                    placement="right"
                    arrow
                    className={classes.tooltip}
                  >
                    <span>
                      <MiIcon
                        path={mdiInformationOutline}
                        size={0.7}
                        color={theme.palette.primary.main}
                      />
                    </span>
                  </Tooltip>
                )}
              </Box>
              {subtitle && (
                <Typography variant="subtitle2" className={classes.subtitle}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {rightIcon &&
              (onRightIconClick ? (
                <MiButton
                  startIcon={{
                    path: rightIcon,
                    iconColor: rightIconColor || theme.palette.grey.main,
                  }}
                  onClick={onRightIconClick}
                  data-testid="right-icon-button"
                  style={{ padding: theme.spacing(1) }}
                />
              ) : (
                <MiIcon
                  path={rightIcon}
                  size={0.8}
                  color={rightIconColor || theme.palette.grey.main}
                  onClick={onRightIconClick}
                  data-testid="right-icon"
                />
              ))}
          </Box>
        </Box>
        {rightAction}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme, { removeMargin, stickyHeader }) => {
  const stickyHeaderStyles = stickyHeader && {
    position: "sticky",
    top: 0,
    zIndex: theme.zIndex.appBar,
  };

  return {
    container: {
      display: "flex",
    },
    containerContained: {
      borderRadius: 12,
      backgroundColor: theme.palette.grey.lightAccent,
      border: `1px solid ${theme.palette.grey.light}`,
      marginBottom: removeMargin ? 0 : 12,
      width: "100%",
      minHeight: MIBUILD_PAGE_HEADER_HEIGHT,
      ...stickyHeaderStyles,
    },
    containerOutlined: {
      borderRadius: 12,
      backgroundColor: theme.palette.white.main,
      border: `1px solid ${theme.palette.grey.accent}`,
      marginBottom: removeMargin ? 0 : 12,
      width: "100%",
      minHeight: MIBUILD_PAGE_HEADER_HEIGHT,
      ...stickyHeaderStyles,
    },
    containerUnderlined: {
      backgroundColor: theme.palette.white.main,
      borderBottom: `1px solid ${theme.palette.grey.accent}`,
      marginBottom: removeMargin ? 0 : 12,
      width: "100%",
      minHeight: MIBUILD_PAGE_HEADER_HEIGHT,
      ...stickyHeaderStyles,
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(2),
      position: "sticky",
      top: 0,
      zIndex: 2,
      width: "100%",
    },
    leftPart: {
      display: "flex",
      flexDirection: "row",
      gap: 8,
      alignItems: "center",
      width: "100%",
      flex: 1,
    },
    title: {
      fontWeight: theme.fontWeight.semiBold,
      color: theme.palette.primary.main,
    },
    subtitle: {
      color: theme.palette.grey.main,
    },
    jobTypeColor: {
      width: 15,
      height: 15,
    },
    divider: {
      marginBottom: 8,
    },
    headerText: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    },
    flexSpaceBetween: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      gap: theme.spacing(2),
    },
    tooltip: {
      display: "flex",
      alignItems: "center",
    },
    titleContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
  };
});
